import { cn } from "@jesus-central-church/shared";
import { type ReactNode } from "react";
import { Image } from "../media/image";
import { ImageHeroContent } from "./image-hero-content";

interface ImageHeroProperties {
    imageAlt: string;
    imageSrc: string;
    title: string;
    subtitle: string;
    children: ReactNode;
    posterImage?: string;
    nextServiceComponent?: ReactNode;
}

/**
 * Checks if a URL points to a video file
 */
const isVideoSource = (src: string): boolean => {
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
    const lowerSrc = src.toLowerCase();
    return videoExtensions.some((ext) => lowerSrc.endsWith(ext));
};

/**
 * ImageHero - Server Component
 *
 * Bold, cinematic hero with brutalist typography.
 * Image/video renders immediately via SSR for fast LCP.
 */
export const ImageHero = ({
    imageAlt,
    imageSrc,
    title,
    subtitle,
    children,
    posterImage,
    nextServiceComponent,
}: ImageHeroProperties) => {
    const isVideo = isVideoSource(imageSrc);


    return (
        <div className={cn("relative h-[80vh] min-h-[550px] max-h-[900px] w-full overflow-hidden")}>
            {/* Background media - renders immediately via SSR */}
            <div className="absolute inset-0">
                {isVideo ? (
                    <video
                        src={imageSrc}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        className="absolute inset-0 h-full w-full object-cover"
                        poster={posterImage}
                        aria-label={imageAlt}
                    />
                ) : (
                    <Image
                        src={imageSrc}
                        alt={imageAlt}
                        fill
                        className="object-cover"
                        sizes="100vw"
                        priority
                        fetchPriority="high"
                        quality={90}
                    />
                )}

                {/* Single overlay with combined gradients for better LCP */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: `
                            linear-gradient(to top, rgba(22,22,21,1) 0%, rgba(22,22,21,0.2) 50%, transparent 100%),
                            linear-gradient(to bottom, rgba(22,22,21,0.5) 0%, transparent 30%),
                            linear-gradient(to right, rgba(22,22,21,0.3) 0%, transparent 20%, transparent 80%, rgba(22,22,21,0.3) 100%),
                            rgba(22,22,21,0.5)
                        `.replaceAll(/\s+/g, ' ')
                    }}
                />
            </div>

            {/* Animated content */}
            <ImageHeroContent title={title} subtitle={subtitle}>
                {children}
            </ImageHeroContent>

            {nextServiceComponent && (
                <div className="absolute bottom-6 right-6 z-20 hidden md:block lg:bottom-8 lg:right-8">
                    {nextServiceComponent}
                </div>
            )}


            {/* Bottom edge - sharp cut */}
            <div className="absolute bottom-0 left-0 right-0 h-px bg-freedom/10" />
        </div>
    );
};
